import { ErrorHandler, Injectable } from '@angular/core';
import * as StackTrace from 'stacktrace-js';
import { LoggingService } from '@app/core/logging/logging.service';
import { isNonProductionEnvironment } from '@app/core/utils';
import { NotificationsService } from 'angular2-notifications';
import { AppConfigService } from '@services/app-config.service';

// Add some of the known errors in this list which we do not want to include in logging.
// For example knownErrors = ['"StatusBar" plugin is not implemented on web']
const knownErrors = [
  'ChunkLoadError: Loading chunk', // Error we get for ion modules in offline mode in web
  'https://esp.aptrinsic.com' // Failed gainsight events due to using app in offline mode
];

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private lastErrorMessage: string;

  constructor(private readonly loggingService: LoggingService,
              private readonly notificationService: NotificationsService,
              private readonly appConfigService: AppConfigService) {
  }

  async handleError(error) {
    let errorString = '';
    if (error?.toString) {
      errorString = error.toString();
    }

    if (!errorString || errorString.indexOf('[object') > -1) {
      try {
        errorString = JSON.stringify(error);
      } catch {
        errorString = 'Unknown error - unable to stringify';
      }
    }

    const message = error?.message ?? errorString;
    if (this.lastErrorMessage === message) {
      throw error;
    }
    this.lastErrorMessage = message;

    let stackTrace: string;

    if (error.stack) {
      stackTrace = error.stack;
    } else {
      const frames = await StackTrace.fromError(error);

      stackTrace = frames
          .splice(0, 10)
          .map(sf => sf.toString())
          .join('\n');
    }

    // We do not want to add logging for some known errors.
    if (knownErrors.some(err => message.toLowerCase().includes(err.toLowerCase()))) {
      throw error;
    }

    this.loggingService.error(message, stackTrace);
    if (isNonProductionEnvironment()) {
      const config = await this.appConfigService.getConfig();
      if (config.showConsoleErrorsAsToasts) {
        this.notificationService.error('Console Error!',
            `${message?.substring(0, 100)}\nIf this error needs to be ignored, add it in ignore condition. Otherwise please fix it.`,
            { timeOut: 5000, clickToClose: true });
      }
    }
    throw error;
  }
}
